import { useEffect, useRef } from 'react'
import {
  createFileRoute,
  redirect,
  useNavigate,
  useParams,
} from '@tanstack/react-router'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { AlertCircle, CheckCircle2, Loader2 } from 'lucide-react'
import { apiFetch, ApiError } from '@/lib/api/client'
import { Button } from '@/components/ui/button'
import { PageMain } from '@/components/page-main'

export const Route = createFileRoute('/invites/$token')({
  beforeLoad: ({ context, location, params }) => {
    if (!context.auth.isLoading && !context.auth.isAuthenticated) {
      throw redirect({ to: '/login', search: { redirect: location.href } })
    }
    // Sanity check the path param shape; the backend re-validates.
    if (!params.token || params.token.length < 16) {
      throw redirect({ to: '/onboarding' })
    }
  },
  component: AcceptInvitePage,
})

function AcceptInvitePage() {
  const { token } = useParams({ from: '/invites/$token' })
  const navigate = useNavigate()
  const qc = useQueryClient()
  const firedRef = useRef(false)

  const accept = useMutation({
    mutationFn: async () => {
      const r = await apiFetch(
        `/api/invites/${encodeURIComponent(token)}/accept`,
        { method: 'POST' },
      )
      if (!r.ok) {
        throw new ApiError(r.status, await r.text())
      }
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['auth', 'session'] })
      await qc.invalidateQueries({ queryKey: ['invites', 'mine'] })
      // Brief pause so the user registers the success state, then in.
      setTimeout(() => navigate({ to: '/dashboard' }), 800)
    },
  })

  // Fire exactly once on mount. StrictMode would otherwise double-call.
  useEffect(() => {
    if (firedRef.current) return
    firedRef.current = true
    accept.mutate()
  }, [accept])

  return (
    <PageMain className="relative min-h-screen flex items-center justify-center overflow-hidden p-6">
      <div className="absolute inset-0 hawk-grid pointer-events-none" aria-hidden />

      <div className="relative w-full max-w-md animate-hawk-rise">
        <div
          role={accept.isError ? 'alert' : 'status'}
          aria-live="polite"
          className="border border-border bg-card/80 backdrop-blur p-8 rounded-md text-center space-y-5"
        >
          {accept.isPending && (
            <>
              <Loader2
                className="size-6 text-primary animate-spin mx-auto"
                strokeWidth={1.5}
                aria-hidden
              />
              <div className="space-y-1">
                <h1 className="font-mono text-[10px] uppercase tracking-[0.32em] text-muted-foreground">
                  Accepting invitation
                </h1>
                <p className="text-sm text-muted-foreground">
                  Verifying the token and adding you to the organization.
                </p>
              </div>
            </>
          )}

          {accept.isSuccess && (
            <>
              <CheckCircle2
                className="size-6 text-primary mx-auto"
                strokeWidth={1.5}
                aria-hidden
              />
              <div className="space-y-1">
                <h1 className="font-mono text-[10px] uppercase tracking-[0.32em] text-primary">
                  Invitation accepted
                </h1>
                <p className="text-sm text-muted-foreground">
                  Redirecting to your dashboard.
                </p>
              </div>
            </>
          )}

          {accept.isError && (
            <>
              <AlertCircle
                className="size-6 text-destructive mx-auto"
                strokeWidth={1.5}
                aria-hidden
              />
              <div className="space-y-1">
                <h1 className="font-mono text-[10px] uppercase tracking-[0.32em] text-destructive">
                  Could not accept
                </h1>
                <p className="text-sm text-muted-foreground">
                  {accept.error instanceof ApiError
                    ? friendlyError(accept.error)
                    : (accept.error as Error).message}
                </p>
              </div>
              <Button
                variant="outline"
                onClick={() => navigate({ to: '/onboarding' })}
              >
                Back to onboarding
              </Button>
            </>
          )}
        </div>
      </div>
    </PageMain>
  )
}

function friendlyError(err: ApiError): string {
  if (err.status === 403) {
    if (err.body.includes('expired')) return 'This invite has expired.'
    if (err.body.includes('already accepted'))
      return 'This invite has already been accepted.'
    if (err.body.includes('different account'))
      return 'This invite was issued for a different email address. Sign in with the right account.'
    return 'You are not allowed to accept this invite.'
  }
  if (err.status === 404) return 'Invite not found. The link may have been revoked.'
  if (err.status === 400) return 'The token in the URL is malformed.'
  return `Server error (${err.status}).`
}
